import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskLine } from "@/components/Reveal";
import { YEAR, contacts } from "@/data/clubs";
import { useScrollTo } from "@/lib/scroll";
import { waHref } from "@/lib/contact";
import { Phone, Megaphone } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];

export const Hero = ({ schoolName = "מעיינות", schoolId = "maayanot", regUrl = "#" }) => {
  const scrollTo = useScrollTo();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden pt-24 pb-10 px-5 md:px-10">
      <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
        <motion.div style={{ y: yText }} className="lg:col-span-7 order-2 lg:order-1">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25, ease }}
            className="mb-7 rounded-2xl border border-accent/30 bg-accent/[0.06] p-4 md:p-5"
            data-testid="registration-announcement"
          >
            <div className="flex items-start gap-3">
              <span className="grid place-items-center w-9 h-9 rounded-full bg-accent text-white shrink-0">
                <Megaphone className="w-4 h-4" />
              </span>
              <div>
                <p className="font-head font-bold text-accent text-base md:text-lg" data-testid="opening-announcement">
                  פעילות החוגים חוזרת!
                </p>
                <p className="mt-0.5 font-head font-semibold text-ink text-sm md:text-base">
                  שימו לב — השבוע הקרוב <span className="text-accent font-semibold">עד 11/10</span> ניתן להגיע לשיעורי ניסיון בתיאום מראש
                </p>
                <p className="mt-2 text-ink/70 text-sm md:text-base leading-relaxed">
                  מחירי החוגים — <span className="font-semibold text-ink">יתעדכנו בחודש אוקטובר!</span>
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <a
                    href={regUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center font-head font-semibold text-sm bg-accent text-white px-5 py-2.5 rounded-full hover:bg-accent-600 transition-colors"
                    data-testid="announcement-register-link"
                  >
                    להרשמה באתר המועצה ←
                  </a>
                  <button
                    onClick={() => scrollTo("register")}
                    className="inline-flex items-center font-head font-semibold text-sm border border-ink/20 px-5 py-2.5 rounded-full hover:border-ink transition-colors"
                    data-testid="announcement-cta"
                  >
                    השארת פרטים / שיעור ניסיון
                  </button>
                </div>
              </div>
            </div>
          </motion.div>

          <MaskLine delay={0.35} className="font-head text-sm md:text-base font-semibold text-accent tracking-wide">
            חוגי ספורט והעשרה • {YEAR}
          </MaskLine>

          <h1 className="font-head font-black tracking-tighter leading-[0.9] text-ink mt-4 text-[clamp(3rem,11vw,8.5rem)]">
            <MaskLine delay={0.45}>ספורט והעשרה</MaskLine>
            <MaskLine delay={0.58} className="text-accent">{schoolName}</MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease }}
            className="mt-7 max-w-xl text-lg md:text-xl text-ink/70 leading-relaxed"
          >
            שישה חוגים, מדריכים מקצועיים ואווירה שמאפשרת לכל ילד לגלות את הכוח שבו — מהמגרש ועד הבמה.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollTo("clubs")}
              className="font-head font-semibold bg-accent text-white px-7 py-3.5 rounded-full hover:bg-accent-600 transition-colors"
              data-testid="hero-explore"
            >
              לגלות את החוגים
            </button>
            <a
              href={regUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-head font-semibold px-7 py-3.5 rounded-full border border-ink/20 hover:border-ink transition-colors"
              data-testid="hero-register"
            >
              להרשמה באתר המועצה
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2, ease }}
            className="mt-6 flex flex-wrap items-center gap-3"
          >
            <span className="text-sm text-ink/50 font-medium">ליצירת קשר:</span>
            {contacts.map((c) => (
              <a
                key={c.phone}
                href={waHref(c.phone, "היי, אשמח לפרטים על החוגים")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold bg-white border border-ink/15 px-4 py-2 rounded-full hover:border-accent hover:text-accent transition-colors"
                data-testid={`hero-contact-${c.phone}`}
              >
                <Phone className="w-4 h-4" />
                {c.name}
                <span className="text-ink/40 tabular-nums" dir="ltr">{c.phone}</span>
              </a>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.5, ease }}
          className="lg:col-span-5 order-1 lg:order-2"
        >
          <div className="relative rounded-[1.5rem] overflow-hidden aspect-[4/5] border border-ink/10">
            <motion.img
              style={{ y: yImg, scale: scaleImg }}
              src="https://images.unsplash.com/photo-1502086223501-7ea6ecd79368?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="ילדים בפעילות ספורט והעשרה"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent" />
            <div className="absolute bottom-5 right-5 left-5 flex items-end justify-between text-white">
              <span className="font-head font-bold text-2xl">6 חוגים</span>
              <span className="text-sm bg-white/15 backdrop-blur px-3 py-1.5 rounded-full">א'–ו'</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
